import { useState, useRef, useCallback } from 'react';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Upload, FileText, Trash2, Download, FileImage, FileArchive } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { ImportDocument, ImportDocumentType } from '../types/import-tracking.types';

// ── Document type options ────────────────────────────────────────

const DOC_TYPE_LABELS: Record<ImportDocumentType, string> = {
  BILL_OF_LADING: 'Conocimiento de Embarque (BL)',
  COMMERCIAL_INVOICE: 'Factura Comercial',
  PACKING_LIST: 'Lista de Empaque',
  CERTIFICATE_OF_ORIGIN: 'Certificado de Origen',
  PHYTOSANITARY: 'Certificado Fitosanitario',
  CUSTOMS_DECLARATION: 'Declaración Aduanera',
  INSURANCE_POLICY: 'Póliza de Seguro',
  OTHER: 'Otro',
};

const DOC_TYPE_OPTIONS = Object.entries(DOC_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

// ── Helpers ──────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType.includes('zip') || mimeType.includes('rar')) return FileArchive;
  return FileText;
}

// ── Component ────────────────────────────────────────────────────

interface DocumentUploaderProps {
  documents: ImportDocument[];
  isLoading?: boolean;
  onUpload: (file: File, type: string, name: string) => Promise<void>;
  onDelete: (docId: string) => Promise<void>;
  /** Descarga el adjunto. Sin esto el botón no se muestra. */
  onDownload?: (doc: ImportDocument) => Promise<void>;
  className?: string;
}

export function DocumentUploader({
  documents,
  isLoading,
  onUpload,
  onDelete,
  onDownload,
  className,
}: DocumentUploaderProps) {
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<string>('OTHER');
  const [docName, setDocName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) {
        setSelectedFile(file);
        if (!docName) setDocName(file.name.replace(/\.[^.]+$/, ''));
      }
    },
    [docName]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!docName) setDocName(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !docName.trim()) return;
    setUploading(true);
    try {
      await onUpload(selectedFile, docType, docName.trim());
      setSelectedFile(null);
      setDocName('');
      setDocType('OTHER');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (docId: string) => {
    confirm({
      title: 'Eliminar documento',
      description: '¿Seguro que querés eliminar este documento? Esta acción no se puede deshacer.',
      variant: 'destructive',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        await onDelete(docId);
      },
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload zone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subir Documento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
            )}
          >
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            {selectedFile ? (
              <p className="text-sm font-medium">
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </p>
            ) : (
              <>
                <p className="text-sm font-medium">
                  Arrastra un archivo aquí o haz clic para seleccionar
                </p>
                <p className="text-xs text-muted-foreground mt-1">PDF, imágenes, ZIP (max 25 MB)</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg,.zip,.rar,.xlsx,.xls,.csv,.doc,.docx"
            />
          </div>

          {/* Form fields */}
          {selectedFile && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo de Documento</Label>
                <SearchableSelect
                  options={DOC_TYPE_OPTIONS}
                  value={docType}
                  onValueChange={setDocType}
                  placeholder="Seleccionar tipo..."
                  searchPlaceholder="Buscar tipo..."
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="Nombre del documento..."
                />
              </div>
            </div>
          )}

          {selectedFile && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedFile(null);
                  setDocName('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                Cancelar
              </Button>
              <Button size="sm" onClick={handleUpload} disabled={uploading || !docName.trim()}>
                {uploading ? 'Subiendo...' : 'Subir Documento'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Documentos ({documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">
              No se han subido documentos
            </p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => {
                const Icon = getFileIcon(doc.mimeType);
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 group"
                  >
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {DOC_TYPE_LABELS[doc.type] || doc.type}
                        </Badge>
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>{new Date(doc.createdAt).toLocaleDateString('es-BO')}</span>
                        {/*
                          Un documento registrado sin archivo es un caso real —
                          "el BL lo tiene el despachante"— y hay que poder
                          distinguirlo de uno que sí se puede abrir.
                        */}
                        {!doc.fileUrl && <span className="text-alerta">sin archivo adjunto</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {onDownload && doc.fileUrl && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void onDownload(doc)}
                              aria-label={`Descargar ${doc.name}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Descargar</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(doc.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Eliminar</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog />
    </div>
  );
}
